import React, { useCallback, useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  useMediaQuery
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SendIcon from '@mui/icons-material/Send'
import EmailIcon from '@mui/icons-material/Email'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import * as reportingService from 'services/reportingService'

const ReportEmailModal = ({
  open,
  onClose,
  bpId,
  reportId,
  buildingName
}) => {
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [shares, setShares] = useState([])
  const [loadingShares, setLoadingShares] = useState(false)
  const [revokingId, setRevokingId] = useState(null)
  const isMobile = useMediaQuery('(max-width: 600px)')

  const fetchShares = useCallback(async () => {
    if (!bpId || !reportId) return
    setLoadingShares(true)
    try {
      const res = await reportingService.listReportShares(bpId, reportId)
      setShares(res.data?.data || [])
    } catch (_) {
      // Non-fatal — the list is informational.
    } finally {
      setLoadingShares(false)
    }
  }, [bpId, reportId])

  useEffect(() => {
    if (open) fetchShares()
  }, [open, fetchShares])

  const handleRevoke = async (shareId) => {
    setRevokingId(shareId)
    try {
      await reportingService.revokeReportShare(bpId, reportId, shareId)
      await fetchShares()
    } catch (_) {
      setError('Failed to revoke access. Please try again.')
    } finally {
      setRevokingId(null)
    }
  }

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const handleSend = async () => {
    if (!validateEmail(email)) {
      setError('Please enter a valid email address')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      await reportingService.sendReportEmail(bpId, reportId, {
        email,
        message: message.trim() || null
      })
      setSuccess(true)
      setEmail('')
      setMessage('')
      fetchShares()
      // Clear the success banner after a beat; stay on the form so the user can
      // see the new recipient appear in the list and send another if needed.
      setTimeout(() => setSuccess(false), 3500)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send email. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setEmail('')
    setMessage('')
    setError(null)
    setSuccess(false)
    onClose()
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && email && !isLoading) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          overflow: 'hidden'
        }
      }}
    >
      <StyledDialogTitle>
        <TitleContent>
          <TitleIconWrapper>
            <EmailIcon />
          </TitleIconWrapper>
          <TitleText>
            <TitleMain>Share Report</TitleMain>
            <TitleSub>Send a link to view the submission</TitleSub>
          </TitleText>
        </TitleContent>
        <CloseButton onClick={handleClose} size="small">
          <CloseIcon />
        </CloseButton>
      </StyledDialogTitle>

      <StyledDialogContent>
          <FormContent>
            <DescriptionCard>
              <DescriptionIcon>
                <EmailIcon />
              </DescriptionIcon>
              <DescriptionText>
                Share the report for <strong>{buildingName}</strong>.
                Each recipient gets a unique link tied to their email address —
                every view, download and source click through that link is
                recorded in the building's <strong>Golden Thread</strong> under
                their email.
              </DescriptionText>
            </DescriptionCard>

            {success && (
              <StyledAlert severity="success" icon={<CheckCircleIcon fontSize="inherit" />} onClose={() => setSuccess(false)}>
                Email sent — the recipient now appears in the list below.
              </StyledAlert>
            )}

            {error && (
              <StyledAlert severity="error" onClose={() => setError(null)}>
                {error}
              </StyledAlert>
            )}

            <FormFields>
              <InputWrapper>
                <InputLabel>
                  <PersonOutlineIcon fontSize="small" />
                  <span>Recipient Email</span>
                  <RequiredBadge>Required</RequiredBadge>
                </InputLabel>
                <StyledTextField
                  type="email"
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter email address"
                  disabled={isLoading}
                  error={!!error && !validateEmail(email)}
                  autoFocus
                  size="medium"
                  variant="outlined"
                />
              </InputWrapper>

              <InputWrapper>
                <InputLabel>
                  <MessageOutlinedIcon fontSize="small" />
                  <span>Personal Message</span>
                  <OptionalBadge>Optional</OptionalBadge>
                </InputLabel>
                <StyledTextField
                  multiline
                  rows={3}
                  fullWidth
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a personal message to include with the report..."
                  disabled={isLoading}
                  inputProps={{ maxLength: 1000 }}
                  variant="outlined"
                />
                <CharacterCount isNearLimit={message.length > 900}>
                  {message.length}/1000
                </CharacterCount>
              </InputWrapper>
            </FormFields>

            <AccessSection>
              <AccessHeader>
                People with access
                <AccessCount>({shares.filter(s => !s.revokedAt).length})</AccessCount>
              </AccessHeader>
              {loadingShares ? (
                <AccessLoading><CircularProgress size={18} sx={{ color: '#D24724' }} /></AccessLoading>
              ) : shares.filter(s => !s.revokedAt).length === 0 ? (
                <AccessEmpty>No one has been sent this report yet.</AccessEmpty>
              ) : (
                <ShareList>
                  {shares.filter(s => !s.revokedAt).map(s => (
                    <ShareRow key={s.id}>
                      <ShareMain>
                        <ShareEmail>{s.email}</ShareEmail>
                        {s.name && <ShareName>{s.name}</ShareName>}
                        <ShareMeta>
                          Sent {s.createdAt ? new Date(s.createdAt).toLocaleDateString('en-GB') : '—'}
                          {' · '}
                          {s.lastAccessedAt ? (
                            <Accessed>
                              <CheckCircleOutlineIcon style={{ fontSize: 13 }} />
                              {' Last viewed '}{new Date(s.lastAccessedAt).toLocaleDateString('en-GB')}
                            </Accessed>
                          ) : (
                            <NotAccessed>Not yet opened</NotAccessed>
                          )}
                        </ShareMeta>
                      </ShareMain>
                      <Tooltip title="Revoke access">
                        <IconButton
                          size="small"
                          onClick={() => handleRevoke(s.id)}
                          disabled={revokingId === s.id}
                        >
                          {revokingId === s.id
                            ? <CircularProgress size={14} sx={{ color: '#999' }} />
                            : <DeleteOutlineIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </ShareRow>
                  ))}
                </ShareList>
              )}
            </AccessSection>
          </FormContent>
      </StyledDialogContent>

      <StyledDialogActions>
        <CancelButton
          onClick={handleClose}
          disabled={isLoading}
        >
          Done
        </CancelButton>
        <SendButton
          variant="contained"
          onClick={handleSend}
          disabled={!email || isLoading}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
        >
          {isLoading ? 'Sending...' : 'Send Report'}
        </SendButton>
      </StyledDialogActions>
    </StyledDialog>
  )
}

export default ReportEmailModal

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`

const StyledDialog = styled(Dialog)`
  && {
    .MuiBackdrop-root {
      backdrop-filter: blur(4px);
      background-color: rgba(0, 0, 0, 0.4);
    }
  }
`

const StyledDialogTitle = styled(DialogTitle)`
  && {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 24px 28px 20px;
    background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}08, transparent);
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};

    @media (max-width: 600px) {
      padding: 20px 20px 16px;
    }
  }
`

const TitleContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const TitleIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, #B83D1F 100%);
  border-radius: 14px;
  color: white;
  box-shadow: 0 4px 12px rgba(210, 71, 36, 0.25);

  svg {
    font-size: 24px;
  }

  @media (max-width: 600px) {
    width: 44px;
    height: 44px;
    border-radius: 12px;

    svg {
      font-size: 22px;
    }
  }
`

const TitleText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const TitleMain = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};

  @media (max-width: 600px) {
    font-size: 18px;
  }
`

const TitleSub = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const CloseButton = styled(IconButton)`
  && {
    margin: -8px -8px 0 0;
    color: ${({ theme }) => theme.colors.grey[200]};
    transition: all 0.2s ease;

    &:hover {
      color: ${({ theme }) => theme.colors.haiti};
      background: ${({ theme }) => theme.colors.lightGrey}40;
    }
  }
`

const StyledDialogContent = styled(DialogContent)`
  && {
    padding: 28px;

    @media (max-width: 600px) {
      padding: 24px 20px;
    }
  }
`

// Success State
const SuccessContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  text-align: center;
  animation: ${fadeIn} 0.4s ease-out;
`

const SuccessIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  border-radius: 50%;
  color: white;
  margin-bottom: 24px;
  animation: ${scaleIn} 0.5s ease-out;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);

  svg {
    font-size: 44px;
  }
`

const SuccessTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 12px;
`

const SuccessText = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 8px;
  line-height: 1.5;

  strong {
    font-weight: 600;
  }
`

const SuccessSubtext = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin: 0;
`

// Form Content
const FormContent = styled.div`
  animation: ${fadeIn} 0.3s ease-out;
`

const DescriptionCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.backgroundColor} 0%, #F8FAFC 100%);
  border-radius: 12px;
  margin-bottom: 24px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const DescriptionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.colors.primary}12;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;

  svg {
    font-size: 20px;
  }
`

const DescriptionText = styled.p`
  color: ${({ theme }) => theme.colors.grey[200]};
  font-size: 14px;
  line-height: 1.6;
  margin: 0;

  strong {
    color: ${({ theme }) => theme.colors.haiti};
    font-weight: 600;
  }
`

const StyledAlert = styled(Alert)`
  && {
    margin-bottom: 20px;
    border-radius: 10px;
  }
`

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const InputWrapper = styled.div`
  position: relative;
`

const InputLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};

  svg {
    color: ${({ theme }) => theme.colors.grey[200]};
    font-size: 18px;
  }
`

const RequiredBadge = styled.span`
  margin-left: auto;
  padding: 3px 10px;
  background: ${({ theme }) => theme.colors.primary}12;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`

const OptionalBadge = styled.span`
  margin-left: auto;
  padding: 3px 10px;
  background: #F3F4F6;
  color: ${({ theme }) => theme.colors.grey[200]};
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`

const StyledTextField = styled(TextField)`
  && {
    .MuiOutlinedInput-root {
      border-radius: 10px;
      background: #ffffff;
      transition: all 0.2s ease;

      &:hover {
        background: #fafafa;
      }

      &.Mui-focused {
        background: #ffffff;
        box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}15;
      }

      fieldset {
        border-color: ${({ theme }) => theme.colors.lightGrey};
        transition: border-color 0.2s ease;
      }

      &:hover fieldset {
        border-color: ${({ theme }) => theme.colors.primary}60;
      }

      &.Mui-focused fieldset {
        border-color: ${({ theme }) => theme.colors.primary};
        border-width: 1px;
      }
    }

    .MuiInputBase-input {
      padding: 14px 16px;
      font-size: 15px;

      &::placeholder {
        color: ${({ theme }) => theme.colors.grey[200]};
        opacity: 0.7;
      }
    }

    .MuiInputBase-multiline {
      padding: 0;
    }
  }
`

const CharacterCount = styled.span`
  position: absolute;
  right: 12px;
  bottom: -20px;
  font-size: 12px;
  color: ${({ theme, isNearLimit }) =>
    isNearLimit ? '#F59E0B' : theme.colors.grey[200]};
  transition: color 0.2s ease;
`

const AccessSection = styled.div`
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
`

const AccessHeader = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  margin-bottom: 10px;
  display: flex;
  align-items: baseline;
  gap: 6px;
`

const AccessCount = styled.span`
  font-weight: 500;
  color: #9ca3af;
  text-transform: none;
  letter-spacing: normal;
`

const AccessLoading = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px;
`

const AccessEmpty = styled.div`
  font-size: 13px;
  color: #9ca3af;
  font-style: italic;
  padding: 4px 0;
`

const ShareList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ShareRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
`

const ShareMain = styled.div`
  flex: 1;
  min-width: 0;
`

const ShareEmail = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  word-break: break-all;
`

const ShareName = styled.div`
  font-size: 12px;
  color: #374151;
`

const ShareMeta = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
`

const Accessed = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: #065f46;
  vertical-align: middle;
`

const NotAccessed = styled.span`
  color: #9ca3af;
  font-style: italic;
`

const StyledDialogActions = styled(DialogActions)`
  && {
    padding: 20px 28px 24px;
    gap: 12px;
    background: linear-gradient(to top, ${({ theme }) => theme.colors.backgroundColor}80, transparent);
    border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};

    @media (max-width: 600px) {
      padding: 16px 20px 20px;
      flex-direction: column;

      > * {
        width: 100%;
        margin: 0 !important;
      }
    }
  }
`

const CancelButton = styled(Button)`
  && {
    padding: 12px 24px;
    border-radius: 10px;
    font-weight: 600;
    text-transform: none;
    color: ${({ theme }) => theme.colors.haiti};
    transition: all 0.2s ease;

    &:hover {
      background: ${({ theme }) => theme.colors.lightGrey}40;
    }
  }
`

const SendButton = styled(Button)`
  && {
    padding: 12px 28px;
    border-radius: 10px;
    font-weight: 600;
    text-transform: none;
    background: ${({ theme }) => theme.colors.secondary};
    color: #ffffff;
    box-shadow: 0 4px 12px rgba(51, 190, 204, 0.3);
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background: #2BA8B9;
      box-shadow: 0 6px 16px rgba(51, 190, 204, 0.4);
      transform: translateY(-1px);
    }

    &:disabled {
      background: ${({ theme }) => theme.colors.grey[200]};
      box-shadow: none;
    }
  }
`
